export type Position = Readonly<{ x: number; y: number }>;
export type Size = Readonly<{ width: number; height: number }>;
export type Element = Readonly<Position & Size & { id: string }>;

const clampFn = (min: number, max: number) => (value: number): number =>
  Math.min(Math.max(value, min), max);

const clampPositionFn = (bounds: Size) => (pos: Position): Position => ({
  x: clampFn(0, bounds.width)(pos.x),
  y: clampFn(0, bounds.height)(pos.y),
});

const centerOfFn = (el: Element): Position => ({
  x: el.x + el.width / 2,
  y: el.y + el.height / 2,
});

const distanceBetweenFn = (a: Position, b: Position): number =>
  Math.sqrt((b.x - a.x) ** 2 + (b.y - a.y) ** 2);

const findClosestElementFn = (target: Position) => (
  elements: readonly Element[]
): Element | undefined =>
  elements.reduce<{ el: Element | undefined; dist: number }>(
    (closest, el) => {
      const dist = distanceBetweenFn(target, centerOfFn(el));
      return dist < closest.dist ? { el, dist } : closest;
    },
    { el: undefined, dist: Infinity }
  ).el;

const moveElementFn = (id: string, delta: Position) => (
  elements: readonly Element[]
): readonly Element[] =>
  elements.map((el) =>
    el.id === id
      ? { ...el, x: el.x + delta.x, y: el.y + delta.y }
      : el
  );

const positionRightOfFn = (point: Position, size: Size): Position => ({
  x: point.x,
  y: point.y - size.height / 2,
});

export const positioning = {
  clamp: clampFn,
  clampPosition: clampPositionFn,
  centerOf: centerOfFn,
  distanceBetween: distanceBetweenFn,
  findClosestElement: findClosestElementFn,
  moveElement: moveElementFn,
  positionRightOf: positionRightOfFn,
} as const;
